import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, clearAuthToken } from '@/App';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Car, Wallet, Clock, LogOut, TrendingUp, AlertTriangle, ArrowLeftRight, XCircle, CreditCard, Repeat, Infinity, Bell, CheckCheck } from 'lucide-react';
import { POLLING_INTERVAL_MS, NOTIFICATION_COLORS, PAYMENT_STATUS_STYLES } from '@/constants';

const OwnerDashboard = () => {
  const navigate = useNavigate();
  const [spots, setSpots] = useState([]);
  const [earnings, setEarnings] = useState({ total_earnings: 0, bookings_count: 0 });
  const [bookings, setBookings] = useState([]);
  const [credits, setCredits] = useState({ credits: 0, transactions: [] });
  const [loading, setLoading] = useState(false);
  const [expandedSpot, setExpandedSpot] = useState(null);
  const [spotUpdates, setSpotUpdates] = useState({});
  const [earlyReturnSpot, setEarlyReturnSpot] = useState(null);
  const [newReturnTime, setNewReturnTime] = useState('');
  const [cancelConfirmSpot, setCancelConfirmSpot] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [spotsRes, earningsRes, bookingsRes, creditsRes, notifsRes] = await Promise.all([
        axios.get(`${API}/owner/spot/status`),
        axios.get(`${API}/owner/earnings`),
        axios.get(`${API}/owner/bookings`),
        axios.get(`${API}/owner/credits`),
        axios.get(`${API}/owner/notifications`)
      ]);
      setSpots(spotsRes.data.spots || []);
      setEarnings(earningsRes.data);
      setBookings(bookingsRes.data.bookings || []);
      setCredits(creditsRes.data);
      setNotifications(notifsRes.data.notifications || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    }
  }, []);

  useEffect(() => {
    const role = localStorage.getItem('role');
    if (!role || role !== 'owner') {
      navigate('/auth/owner');
      return;
    }
    fetchData();
    const interval = setInterval(fetchData, POLLING_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [navigate, fetchData]);

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/owner/notifications/read-all`);
      setNotifications([]);
      setShowNotifications(false);
    } catch (error) {
      toast.error('Failed to clear notifications');
    }
  };

  const toggleSpot = async (spotNumber, isAvailable, availableFrom, availableTo, availabilityMode, recurringStartTime, recurringEndTime, isFree, ratePerHour) => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/owner/spot/toggle`,
        {
          spot_number: spotNumber,
          is_available: isAvailable,
          availability_mode: availabilityMode || 'fixed',
          available_from: availableFrom || null,
          available_to: availableTo || null,
          recurring_start_time: recurringStartTime || null,
          recurring_end_time: recurringEndTime || null,
          is_free: isFree || false,
          rate_per_hour: ratePerHour || 50
        }
      );
      toast.success(`Spot ${isAvailable ? 'marked available' : 'marked unavailable'}`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update spot');
    } finally {
      setLoading(false);
    }
  };

  const handleSpotToggle = (spotNumber, currentlyAvailable) => {
    if (currentlyAvailable) {
      toggleSpot(spotNumber, false, null, null, 'fixed', null, null, false, 0);
    } else {
      setExpandedSpot(spotNumber);
      setSpotUpdates({ ...spotUpdates, [spotNumber]: { available_from: '', available_to: '', mode: 'fixed', recurring_start: '', recurring_end: '', is_free: false, rate_per_hour: 50 } });
    }
  };

  const handleTimeUpdate = (spotNumber, field, value) => {
    setSpotUpdates({ ...spotUpdates, [spotNumber]: { ...spotUpdates[spotNumber], [field]: value } });
  };

  const handleSaveAvailability = async (spotNumber) => {
    const update = spotUpdates[spotNumber];
    if (!update) {
      toast.error('Please configure availability settings');
      return;
    }

    const mode = update.mode || 'fixed';
    const isFree = update.is_free || false;
    const rate = isFree ? 0 : (parseFloat(update.rate_per_hour) || 50);

    if (mode === 'fixed') {
      if (!update.available_from || !update.available_to) {
        toast.error('Please select both from and to times');
        return;
      }
      await toggleSpot(spotNumber, true, update.available_from, update.available_to, 'fixed', null, null, isFree, rate);
    } else if (mode === 'indefinite') {
      await toggleSpot(spotNumber, true, null, null, 'indefinite', null, null, isFree, rate);
    } else if (mode === 'recurring') {
      if (!update.recurring_start || !update.recurring_end) {
        toast.error('Please select daily start and end times');
        return;
      }
      await toggleSpot(spotNumber, true, null, null, 'recurring', update.recurring_start, update.recurring_end, isFree, rate);
    }

    setExpandedSpot(null);
    setSpotUpdates({});
  };

  const handleEarlyReturn = async (spotNumber) => {
    if (!newReturnTime) {
      toast.error('Please select new return time');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/owner/update-return-time`,
        { spot_number: spotNumber, new_return_time: newReturnTime }
      );
      toast.success(res.data.message);
      setEarlyReturnSpot(null);
      setNewReturnTime('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update return time');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelAvailability = async (spotNumber) => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/owner/cancel-availability`,
        { spot_number: spotNumber, reason: 'Owner needs spot urgently' }
      );
      const data = res.data;
      let msg = data.message;
      if (data.failed_reallocation && data.failed_reallocation.length > 0) {
        const vehicles = data.failed_reallocation.map(f => f.vehicle_number).join(', ');
        msg += ` Vehicles ended without reallocation: ${vehicles}`;
      }
      if (data.reallocated > 0) {
        msg += ' Visitors have been moved to alternative spots.';
      }
      toast.success(msg);
      setCancelConfirmSpot(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel availability');
    } finally {
      setLoading(false);
    }
  };

  const getActiveBookingForSpot = (spotId) => bookings.find(b => b.spot_id === spotId && b.status === 'active');

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`).catch(() => {});
    clearAuthToken();
    localStorage.clear();
    navigate('/');
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F4F4F5' }}>
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
        <div className="max-w-7xl mx-auto px-6 py-6 flex items-center justify-between">
          <div>
            <h1
              className="text-2xl sm:text-3xl tracking-tight font-bold"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}
              data-testid="owner-dashboard-title"
            >
              Owner Dashboard
            </h1>
            <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A', fontSize: '14px' }}>
              Manage your parking spots and earnings
            </p>
          </div>
          <div className="flex items-center gap-3">
            {/* Notification Bell */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 border transition-colors hover:bg-gray-50"
                style={{ borderColor: '#E4E4E7' }}
                data-testid="notification-bell"
              >
                <Bell size={20} style={{ color: notifications.length > 0 ? '#09090B' : '#A1A1AA' }} />
                {notifications.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 flex items-center justify-center text-xs font-bold" style={{ backgroundColor: '#DC2626', color: '#FFFFFF', borderRadius: '50%' }} data-testid="notification-count">
                    {notifications.length}
                  </span>
                )}
              </button>
              {showNotifications && (
                <div className="absolute right-0 top-12 w-80 max-h-96 overflow-y-auto border shadow-lg z-50" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }} data-testid="notification-panel">
                  <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: '#E4E4E7' }}>
                    <p className="text-sm font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>Notifications</p>
                    {notifications.length > 0 && (
                      <button onClick={handleMarkAllRead} className="flex items-center gap-1 text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#3B82F6' }} data-testid="mark-all-read">
                        <CheckCheck size={12} /> Mark all read
                      </button>
                    )}
                  </div>
                  {notifications.length === 0 ? (
                    <div className="p-4 text-center">
                      <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#A1A1AA' }}>No new notifications</p>
                    </div>
                  ) : (
                    notifications.map((n) => (
                      <div key={n.id} className="p-3 border-b" style={{ borderColor: '#F4F4F5', backgroundColor: NOTIFICATION_COLORS[n.type] || NOTIFICATION_COLORS.checkout }} data-testid={`notification-${n.id}`}>
                        <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>{n.message}</p>
                        <p className="text-xs mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#A1A1AA' }}>{new Date(n.created_at).toLocaleString()}</p>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <Button onClick={handleLogout} variant="outline" data-testid="logout-button" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <LogOut size={16} className="mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '1px' }} data-testid="earnings-card">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ backgroundColor: '#FBBF24', padding: '12px' }}>
                <Wallet size={24} style={{ color: '#09090B' }} />
              </div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: '#71717A' }}>Total Earnings</p>
            </div>
            <p className="text-4xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
              {'\u20B9'}{earnings.total_earnings.toFixed(2)}
            </p>
            <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A', fontSize: '14px', marginTop: '8px' }}>
              From {earnings.bookings_count} paid bookings
            </p>
          </div>

          <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '1px' }} data-testid="credits-card">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ backgroundColor: credits.credits >= 0 ? '#10B981' : '#DC2626', padding: '12px' }}>
                <CreditCard size={24} style={{ color: '#FFFFFF' }} />
              </div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: '#71717A' }}>Credit Balance</p>
            </div>
            <p className="text-4xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: credits.credits >= 0 ? '#09090B' : '#DC2626' }}>
              {credits.credits >= 0 ? '+' : ''}{'\u20B9'}{credits.credits.toFixed(2)}
            </p>
            <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A', fontSize: '14px', marginTop: '8px' }}>
              Adjustments from reallocations
            </p>
          </div>

          <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '1px' }} data-testid="spots-card">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ backgroundColor: '#09090B', padding: '12px' }}>
                <Car size={24} style={{ color: '#FFFFFF' }} />
              </div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: '#71717A' }}>My Spots</p>
            </div>
            <p className="text-4xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{spots.length}</p>
            <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A', fontSize: '14px', marginTop: '8px' }}>
              {spots.filter(s => s.is_available).length} currently available
            </p>
          </div>

          <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '1px' }} data-testid="bookings-card">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ backgroundColor: '#10B981', padding: '12px' }}>
                <TrendingUp size={24} style={{ color: '#FFFFFF' }} />
              </div>
              <p className="text-xs tracking-[0.2em] uppercase font-bold" style={{ color: '#71717A' }}>Active Bookings</p>
            </div>
            <p className="text-4xl font-black" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
              {bookings.filter(b => b.status === 'active').length}
            </p>
          </div>
        </div>

        {/* My Spots List */}
        <div className="p-6 border mb-8" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '1px' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
            My Parking Spots
          </h2>
          {spots.length === 0 ? (
            <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>No spots registered. Please contact admin.</p>
          ) : (
            <div className="space-y-4">
              {spots.map((spot) => {
                const activeBooking = getActiveBookingForSpot(spot.id);
                return (
                  <div key={spot.id} className="border" style={{ borderColor: '#E4E4E7', borderWidth: '1px' }} data-testid={`spot-item-${spot.spot_number}`}>
                    <div className="flex items-center justify-between p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 flex items-center justify-center" style={{ backgroundColor: spot.is_available ? '#FBBF24' : '#E4E4E7' }}>
                          <Car size={20} style={{ color: '#09090B' }} />
                        </div>
                        <div>
                          <p className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{spot.spot_number}</p>
                          <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A', fontSize: '14px' }}>
                            {spot.is_available ? (
                              <>
                                {spot.is_free && (
                                  <span className="inline-block text-xs font-bold px-2 py-0.5 mb-1 mr-2" style={{ backgroundColor: '#10B981', color: '#FFFFFF' }} data-testid={`free-badge-${spot.spot_number}`}>
                                    FREE
                                  </span>
                                )}
                                {!spot.is_free && spot.rate_per_hour > 0 && (
                                  <span className="inline-block text-xs font-bold px-2 py-0.5 mb-1 mr-2" style={{ backgroundColor: '#3B82F6', color: '#FFFFFF' }} data-testid={`rate-badge-${spot.spot_number}`}>
                                    {'\u20B9'}{spot.rate_per_hour}/hr
                                  </span>
                                )}
                                {spot.availability_mode === 'indefinite' && (
                                  <span className="flex items-center gap-1">
                                    <Infinity size={14} /> Always Available (until turned off)
                                  </span>
                                )}
                                {spot.availability_mode === 'recurring' && (
                                  <span className="flex items-center gap-1">
                                    <Repeat size={14} /> Daily: {spot.recurring_start_time || '?'} - {spot.recurring_end_time || '?'} IST
                                  </span>
                                )}
                                {(!spot.availability_mode || spot.availability_mode === 'fixed') && (
                                  <>
                                    Available: {spot.available_from ? new Date(spot.available_from).toLocaleString() : 'Now'}
                                    {' \u2192 '}
                                    {spot.available_to ? new Date(spot.available_to).toLocaleString() : 'Indefinite'}
                                  </>
                                )}
                                {spot.buffer_time && spot.availability_mode !== 'indefinite' && spot.availability_mode !== 'recurring' && (
                                  <span className="block text-xs" style={{ color: '#DC2626' }}>
                                    (Buffer: till {new Date(spot.buffer_time).toLocaleString()})
                                  </span>
                                )}
                              </>
                            ) : 'Not Available'}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleSpotToggle(spot.spot_number, spot.is_available)}
                        data-testid={`toggle-spot-${spot.spot_number}`}
                        className="px-4 py-2 text-sm font-bold min-w-[120px] transition-all active:scale-95"
                        style={{
                          backgroundColor: spot.is_available ? '#DC2626' : '#09090B',
                          color: '#FFFFFF',
                          fontFamily: 'Cabinet Grotesk, sans-serif',
                          border: 'none',
                          cursor: 'pointer'
                        }}
                      >
                        {spot.is_available ? 'Turn Off' : 'Make Available'}
                      </button>
                    </div>

                    {/* Active Booking Badge */}
                    {activeBooking && (
                      <div className="mx-4 mb-3 p-3 flex items-center gap-3" style={{ backgroundColor: '#FEF3C7', border: '1px solid #F59E0B' }} data-testid={`active-booking-badge-${spot.spot_number}`}>
                        <Car size={16} style={{ color: '#92400E' }} />
                        <div style={{ flex: 1 }}>
                          <p className="text-sm font-bold" style={{ color: '#92400E', fontFamily: 'JetBrains Mono, monospace' }}>
                            {activeBooking.vehicle_number} parked here
                          </p>
                          <p className="text-xs" style={{ color: '#92400E', fontFamily: 'IBM Plex Sans, sans-serif' }}>
                            Exit by: {new Date(activeBooking.estimated_exit).toLocaleTimeString()}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Early Return / Cancel Actions for available spots */}
                    {spot.is_available && (
                      <div className="px-4 pb-4 flex gap-2 flex-wrap">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setEarlyReturnSpot(spot.spot_number); setNewReturnTime(''); setCancelConfirmSpot(null); }}
                          data-testid={`early-return-btn-${spot.spot_number}`}
                          style={{ fontFamily: 'IBM Plex Sans, sans-serif', borderColor: '#F59E0B', color: '#92400E' }}
                        >
                          <Clock size={14} className="mr-1" /> Return Early
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => { setCancelConfirmSpot(spot.spot_number); setEarlyReturnSpot(null); }}
                          data-testid={`cancel-availability-btn-${spot.spot_number}`}
                          style={{ fontFamily: 'IBM Plex Sans, sans-serif', borderColor: '#DC2626', color: '#DC2626' }}
                        >
                          <XCircle size={14} className="mr-1" /> Cancel Availability
                        </Button>
                      </div>
                    )}

                    {/* Early Return Form */}
                    {earlyReturnSpot === spot.spot_number && (
                      <div className="p-4 border-t" style={{ backgroundColor: '#FFFBEB', borderColor: '#F59E0B' }} data-testid={`early-return-form-${spot.spot_number}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <AlertTriangle size={16} style={{ color: '#92400E' }} />
                          <p className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#92400E' }}>
                            Set Revised Return Time
                          </p>
                        </div>
                        <p className="text-xs mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#92400E' }}>
                          {activeBooking
                            ? 'Active visitor will be auto-reallocated to another available spot. No extra charge to visitor.'
                            : 'This will shorten the availability window for future bookings.'}
                        </p>
                        <div className="flex gap-2 items-end">
                          <div className="flex-1">
                            <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif', fontSize: '12px' }}>New Return Time</Label>
                            <Input
                              type="datetime-local"
                              step="60"
                              value={newReturnTime}
                              onChange={(e) => setNewReturnTime(e.target.value)}
                              data-testid={`early-return-time-${spot.spot_number}`}
                              className="mt-1"
                            />
                          </div>
                          <Button
                            onClick={() => handleEarlyReturn(spot.spot_number)}
                            disabled={loading}
                            data-testid={`confirm-early-return-${spot.spot_number}`}
                            size="sm"
                            style={{ backgroundColor: '#F59E0B', color: '#09090B', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}
                          >
                            {loading ? 'Updating...' : 'Confirm'}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setEarlyReturnSpot(null)} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Cancel Availability Confirmation */}
                    {cancelConfirmSpot === spot.spot_number && (
                      <div className="p-4 border-t" style={{ backgroundColor: '#FEF2F2', borderColor: '#DC2626' }} data-testid={`cancel-confirm-${spot.spot_number}`}>
                        <div className="flex items-center gap-2 mb-3">
                          <XCircle size={16} style={{ color: '#DC2626' }} />
                          <p className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#DC2626' }}>
                            Cancel Spot Availability?
                          </p>
                        </div>
                        <p className="text-xs mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#7F1D1D' }}>
                          {activeBooking
                            ? 'Active visitor will be auto-reallocated. A credit adjustment will apply to your next payout.'
                            : 'This spot will be marked as unavailable immediately.'}
                        </p>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleCancelAvailability(spot.spot_number)}
                            disabled={loading}
                            data-testid={`confirm-cancel-${spot.spot_number}`}
                            size="sm"
                            style={{ backgroundColor: '#DC2626', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}
                          >
                            {loading ? 'Cancelling...' : 'Yes, Cancel'}
                          </Button>
                          <Button variant="outline" size="sm" onClick={() => setCancelConfirmSpot(null)} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                            No, Keep
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Expandable time selection when toggling on */}
                    {expandedSpot === spot.spot_number && !spot.is_available && (
                      <div className="p-4 border-t" style={{ backgroundColor: '#F4F4F5', borderColor: '#E4E4E7' }}>
                        <p className="text-sm font-bold mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>Set Availability</p>

                        {/* Mode Selector */}
                        <div className="flex gap-2 mb-4 flex-wrap">
                          {[
                            { key: 'fixed', label: 'Fixed Duration', icon: <Clock size={14} /> },
                            { key: 'indefinite', label: 'Always Available', icon: <Infinity size={14} /> },
                            { key: 'recurring', label: 'Daily Recurring', icon: <Repeat size={14} /> },
                          ].map(({ key, label, icon }) => (
                            <button
                              key={key}
                              onClick={() => handleTimeUpdate(spot.spot_number, 'mode', key)}
                              data-testid={`mode-${key}-${spot.spot_number}`}
                              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium transition-all"
                              style={{
                                backgroundColor: (spotUpdates[spot.spot_number]?.mode || 'fixed') === key ? '#09090B' : '#FFFFFF',
                                color: (spotUpdates[spot.spot_number]?.mode || 'fixed') === key ? '#FFFFFF' : '#71717A',
                                border: '1px solid',
                                borderColor: (spotUpdates[spot.spot_number]?.mode || 'fixed') === key ? '#09090B' : '#E4E4E7',
                                fontFamily: 'IBM Plex Sans, sans-serif',
                              }}
                            >
                              {icon} {label}
                            </button>
                          ))}
                        </div>

                        {/* Fixed Mode - Date + Time pickers */}
                        {(spotUpdates[spot.spot_number]?.mode || 'fixed') === 'fixed' && (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Available From</Label>
                              <Input
                                type="datetime-local"
                                step="60"
                                value={spotUpdates[spot.spot_number]?.available_from || ''}
                                onChange={(e) => handleTimeUpdate(spot.spot_number, 'available_from', e.target.value)}
                                data-testid={`available-from-${spot.spot_number}`}
                                className="mt-2 p-3 border-2"
                              />
                            </div>
                            <div>
                              <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Available To (Return Time)</Label>
                              <Input
                                type="datetime-local"
                                step="60"
                                value={spotUpdates[spot.spot_number]?.available_to || ''}
                                onChange={(e) => handleTimeUpdate(spot.spot_number, 'available_to', e.target.value)}
                                data-testid={`available-to-${spot.spot_number}`}
                                className="mt-2 p-3 border-2"
                              />
                              <p className="text-xs mt-1" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}>
                                *2-hour buffer auto-applied (visitors can book till 2 hours before)
                              </p>
                            </div>
                          </div>
                        )}

                        {/* Indefinite Mode */}
                        {spotUpdates[spot.spot_number]?.mode === 'indefinite' && (
                          <div className="mb-4 p-4" style={{ backgroundColor: '#ECFDF5', border: '1px solid #10B981' }}>
                            <div className="flex items-center gap-2">
                              <Infinity size={18} style={{ color: '#059669' }} />
                              <p className="text-sm font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#065F46' }}>
                                Spot will be available 24/7 until you manually turn it off.
                              </p>
                            </div>
                            <p className="text-xs mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#065F46' }}>
                              Ideal for owners who don't own a car or won't need the spot for an extended period.
                            </p>
                          </div>
                        )}

                        {/* Recurring Mode */}
                        {spotUpdates[spot.spot_number]?.mode === 'recurring' && (
                          <div className="mb-4">
                            <div className="p-3 mb-3" style={{ backgroundColor: '#EFF6FF', border: '1px solid #3B82F6' }}>
                              <div className="flex items-center gap-2">
                                <Repeat size={16} style={{ color: '#1D4ED8' }} />
                                <p className="text-sm font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#1E3A5F' }}>
                                  Available every day at the same time window.
                                </p>
                              </div>
                              <p className="text-xs mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#1E3A5F' }}>
                                Great for daily commuters who leave at a fixed time.
                              </p>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Daily Start Time (IST)</Label>
                                <Input
                                  type="time"
                                  value={spotUpdates[spot.spot_number]?.recurring_start || ''}
                                  onChange={(e) => handleTimeUpdate(spot.spot_number, 'recurring_start', e.target.value)}
                                  data-testid={`recurring-start-${spot.spot_number}`}
                                  className="mt-2 p-3 border-2"
                                />
                              </div>
                              <div>
                                <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Daily End Time (IST)</Label>
                                <Input
                                  type="time"
                                  value={spotUpdates[spot.spot_number]?.recurring_end || ''}
                                  onChange={(e) => handleTimeUpdate(spot.spot_number, 'recurring_end', e.target.value)}
                                  data-testid={`recurring-end-${spot.spot_number}`}
                                  className="mt-2 p-3 border-2"
                                />
                                <p className="text-xs mt-1" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}>
                                  *2-hour buffer auto-applied daily
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Rate Per Hour */}
                        {!spotUpdates[spot.spot_number]?.is_free && (
                          <div className="mb-4">
                            <Label style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Your Rate ({'\u20B9'} per hour)</Label>
                            <Input
                              type="number"
                              min="0"
                              step="5"
                              value={spotUpdates[spot.spot_number]?.rate_per_hour ?? 50}
                              onChange={(e) => handleTimeUpdate(spot.spot_number, 'rate_per_hour', e.target.value)}
                              data-testid={`rate-input-${spot.spot_number}`}
                              className="mt-2 p-3 border-2 w-40"
                              placeholder="e.g. 50"
                            />
                            <p className="text-xs mt-1" style={{ color: '#71717A', fontFamily: 'IBM Plex Sans, sans-serif' }}>
                              You earn 60% ({'\u20B9'}{Math.round((parseFloat(spotUpdates[spot.spot_number]?.rate_per_hour) || 50) * 0.6)}/hr). Society gets 30%, ParkShare 10%.
                            </p>
                          </div>
                        )}

                        {/* Free Parking Toggle */}
                        <div className="flex items-center justify-between p-3 mb-4 border" style={{ borderColor: '#E4E4E7', backgroundColor: spotUpdates[spot.spot_number]?.is_free ? '#ECFDF5' : '#FFFFFF' }}>
                          <div>
                            <p className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                              Offer for Free
                            </p>
                            <p className="text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                              No charges for visitors. No earnings for this spot.
                            </p>
                          </div>
                          <Switch
                            checked={spotUpdates[spot.spot_number]?.is_free || false}
                            onCheckedChange={(checked) => handleTimeUpdate(spot.spot_number, 'is_free', checked)}
                            data-testid={`free-toggle-${spot.spot_number}`}
                          />
                        </div>

                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleSaveAvailability(spot.spot_number)}
                            disabled={loading}
                            data-testid={`save-availability-${spot.spot_number}`}
                            className="flex-1 transition-transform hover:-translate-y-1"
                            style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}
                          >
                            {loading ? 'Saving...' : 'Save Availability'}
                          </Button>
                          <Button onClick={() => setExpandedSpot(null)} variant="outline" data-testid={`cancel-time-${spot.spot_number}`} style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Credit Transactions */}
        {credits.transactions.length > 0 && (
          <div className="p-6 border mb-8" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '1px' }}>
            <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
              Credit Adjustments
            </h2>
            <div className="space-y-2">
              {credits.transactions.map((txn) => (
                <div key={txn.id} className="flex items-center justify-between p-3 border" style={{ borderColor: '#E4E4E7' }} data-testid={`credit-txn-${txn.id}`}>
                  <div className="flex items-center gap-3">
                    <ArrowLeftRight size={16} style={{ color: txn.amount >= 0 ? '#10B981' : '#DC2626' }} />
                    <div>
                      <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>{txn.reason}</p>
                      <p className="text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                        {new Date(txn.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <p className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: txn.amount >= 0 ? '#10B981' : '#DC2626' }}>
                    {txn.amount >= 0 ? '+' : ''}{'\u20B9'}{txn.amount.toFixed(2)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Bookings */}
        <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '1px' }}>
          <h2 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>
            Recent Bookings
          </h2>
          {bookings.length === 0 ? (
            <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>No bookings yet</p>
          ) : (
            <div className="space-y-3">
              {bookings.slice(0, 5).map((booking) => (
                <div key={booking.id} className="flex items-center justify-between p-4 border" style={{ borderColor: '#E4E4E7' }} data-testid={`booking-item-${booking.id}`}>
                  <div>
                    <p className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
                      {booking.vehicle_number}
                    </p>
                    <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A', fontSize: '14px' }}>
                      {booking.spot_number && <span className="mr-2">{booking.spot_number}</span>}
                      {new Date(booking.check_in).toLocaleString()}
                    </p>
                    {booking.reallocated && (
                      <span className="text-xs px-2 py-0.5 mt-1 inline-block" style={{ backgroundColor: '#DBEAFE', color: '#1E40AF', fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        Reallocated
                      </span>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>
                      {'\u20B9'}{booking.owner_share !== undefined ? booking.owner_share.toFixed(0) : booking.amount}
                    </p>
                    <p className="text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
                      of {'\u20B9'}{booking.amount} total
                    </p>
                    <span
                      className="text-xs px-2 py-1"
                      style={{
                        backgroundColor: (PAYMENT_STATUS_STYLES[booking.payment_status] || PAYMENT_STATUS_STYLES[booking.status] || PAYMENT_STATUS_STYLES.default).bg,
                        color: (PAYMENT_STATUS_STYLES[booking.payment_status] || PAYMENT_STATUS_STYLES.default).color,
                        fontFamily: 'IBM Plex Sans, sans-serif'
                      }}
                    >
                      {booking.payment_status === 'paid' ? 'paid' : booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default OwnerDashboard;
